import { defineStore } from 'pinia'
import { useAuthStore } from './auth'

import { defaultState, defaultActions } from './_global'

export const useEventsStore = defineStore('eventsStore', {
  state: () => ({
    endpoint: 'events',
    enums: null,
    token: null,
    registration: null,
    registrationData: null,
    prices: null,
    guests: null,
    activities: null,
    pendingExcused: false,
    participants: [],
    participantsTotal: 0,
    registrationsTotal: 0,
    participantsLoading: false,
    participantsError: null,
    participantProfile: null,
    participantProfileLoading: false,
    ...defaultState
  }),
  actions: {
    ...defaultActions,
    setPendingExcused(value) {
      this.pendingExcused = !!value
    },
    async getEnums() {
      const config = useRuntimeConfig()
      try {
        this.enums = null
        const response = await $fetch(`${config.public.strapiApi}/${this.endpoint}/get-enums`, {
          method: 'GET'
        })
        this.enums = response
        return true
      } catch (error) {
        console.error(error?.response)
      }
    },
    async getEventToken(event_id, config) {
      const authStore = useAuthStore()
      const userToken = authStore.token
      try {
        const response = await $fetch(`${config.public.strapiApi}/${this.endpoint}/get-token`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${userToken}`
          },
          body: {
            event_id
          }
        })

        if (response.error) {
          const error = new Error(response.error)
          error.response = response
          error.data = response
          throw error
        }

        this.token = response.token
        return true
      } catch (error) {
        console.error(error?.response)
        throw error
      }
    },
    async getEventRegistration(config) {
      const authStore = useAuthStore()
      const userToken = authStore.token
      try {
        const response = await $fetch(`${config.public.strapiApi}/${this.endpoint}/get-registration`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${userToken}`
          },
          body: {
            event_token: this.token
          }
        })
        this.registration = response

        if (this.registration?.id) {
          const strapiResponse = await $fetch(`${config.public.strapiApi}/event-registrations`, {
            method: 'GET',
            headers: {
              Authorization: `Bearer ${userToken}`
            },
            params: {
              'filters[sugar_id][$eq]': this.registration.id
            }
          })
          this.registrationData = strapiResponse?.data?.[0] || null
        }

        return true
      } catch (error) {
        console.error(error?.response)
      }
    },
    /**
     * Fetch the attendees of an event who consented to appear in the participants list.
     * The endpoint is guarded server-side: only a paid registrant of this event gets a 200.
     */
    async getEventParticipants(config, documentId) {
      const authStore = useAuthStore()
      const userToken = authStore.token

      // Reset up front: the store is shared, so a previous event's list must not linger.
      this.participants = []
      this.participantsTotal = 0
      this.registrationsTotal = 0
      this.participantsError = null
      this.participantsLoading = true
      try {
        const response = await $fetch(`${config.public.strapiApi}/${this.endpoint}/${documentId}/participants`, {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${userToken}`
          }
        })
        this.participants = response?.data || []
        this.participantsTotal = response?.meta?.total ?? this.participants.length
        this.registrationsTotal = response?.meta?.registrationsTotal ?? 0
        return true
      } catch (error) {
        this.participants = []
        this.participantsTotal = 0
        this.registrationsTotal = 0
        this.participantsError = error?.data?.error || error?.response?._data?.error || 'unknown'
        console.error(error?.data || error)
        return false
      } finally {
        this.participantsLoading = false
      }
    },
    /**
     * How many attendees of an event consented to be listed. Public endpoint, so the event
     * page can decide whether to show the participants tab before the user is known.
     * @return {Promise<number>}
     */
    async getEventParticipantsCount(config, documentId) {
      try {
        const response = await $fetch(`${config.public.strapiApi}/${this.endpoint}/${documentId}/participants/count`, {
          method: 'GET'
        })
        return response?.total || 0
      } catch (error) {
        console.error(error?.data || error)
        return 0
      }
    },
    /**
     * Fetch one participant's full profile, on modal open.
     * Kept out of the list response so a single request can never hand over every attendee's
     * email and phone number at once.
     */
    async getEventParticipant(config, documentId, participantDocumentId) {
      const authStore = useAuthStore()
      const userToken = authStore.token

      this.participantProfile = null
      this.participantProfileLoading = true
      try {
        const response = await $fetch(
          `${config.public.strapiApi}/${this.endpoint}/${documentId}/participants/${participantDocumentId}`,
          {
            method: 'GET',
            headers: {
              Authorization: `Bearer ${userToken}`
            }
          }
        )
        this.participantProfile = response?.data || null
        return true
      } catch (error) {
        console.error(error?.data || error)
        return false
      } finally {
        this.participantProfileLoading = false
      }
    },
    async updateEventRegistration(config, eventRegistrationId, data) {
      const authStore = useAuthStore()
      const userToken = authStore.token

      if (Number(this.registration?.canceled_c) === 1 && data?.excused !== true) {
        return false
      }

      try {
        const body = {
          event_registration_id: eventRegistrationId,
          program_package_id: data?.programPackageId || null,
          program_packages_rep: data?.programPackagesRep || null,
          place_id: data?.placeId || null,
          arrival_date: data?.arrivalDate || null,
          departure_date: data?.departureDate || null,
          arrival_fly_number: data?.arrivalFlyNumber || null,
          departure_fly_number: data?.departureFlyNumber || null,
          specific_requirements: data?.specificRequirements || null,
          privacy_validated: data?.privacyValidated || null,
          cdg_validated: data?.cdgValidated || null,
          participantslist: data?.participantslist ?? null,
          excused: data?.excused ?? null
        }
        await $fetch(`${config.public.strapiApi}/${this.endpoint}/update-registration`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${userToken}`
          },
          body
        })
        return true
      } catch (error) {
        console.error(error?.response)
      }
    },
    /**
     * Mark the registration as submitted so the register form locks on later loads.
     * @param {*} config
     * @param {*} registrationId
     * @return {Promise<Object>}
     */
    async markRegistrationSubmitted(config, registrationId) {
      const authStore = useAuthStore()
      const userToken = authStore.token

      const response = await $fetch(`${config.public.strapiApi}/${this.endpoint}/mark-registration-submitted`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${userToken}`
        },
        body: {
          event_registration_id: registrationId
        }
      })

      return response
    },
    async getEventPrice(config) {
      const authStore = useAuthStore()
      const userToken = authStore.token
      try {
        const response = await $fetch(`${config.public.strapiApi}/${this.endpoint}/get-price`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${userToken}`
          },
          body: {
            event_registration_id: this.registration.id
          }
        })
        this.prices = response
        return true
      } catch (error) {
        console.error(error?.response)
      }
    },
    async getEventGuests(config) {
      const authStore = useAuthStore()
      const userToken = authStore.token
      try {
        const response = await $fetch(`${config.public.strapiApi}/${this.endpoint}/get-registration-guests`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${userToken}`
          },
          body: {
            event_registration_id: this.registration.id
          }
        })
        this.guests = response
        return true
      } catch (error) {
        console.error(error?.response)
      }
    },
    async setEventGuests(config, guestData) {
      const authStore = useAuthStore()
      const userToken = authStore.token
      try {
        await $fetch(`${config.public.strapiApi}/${this.endpoint}/set-registration-guests`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${userToken}`
          },
          body: {
            event_registration_id: this.registration.id,
            guest: guestData
          }
        })
        return true
      } catch (error) {
        console.error(error?.response)
      }
    },
    async setGuestActivities(config, activityId, guestId) {
      const authStore = useAuthStore()
      const userToken = authStore.token
      try {
        const response = await $fetch(`${config.public.strapiApi}/${this.endpoint}/set-guest-activities`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${userToken}`
          },
          body: {
            activity_registration_id: activityId,
            guest_id: guestId
          }
        })
        return response
      } catch (error) {
        console.error(error?.response)
      }
    },
    async deleteGuestActivities(config, accompanyingActivityId) {
      const authStore = useAuthStore()
      const userToken = authStore.token
      try {
        const response = await $fetch(`${config.public.strapiApi}/${this.endpoint}/delete-guest-activities`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${userToken}`
          },
          body: {
            accompanying_activity_id: accompanyingActivityId
          }
        })

        if (response.error) {
          const error = new Error(response.error_message || response.error)
          error.response = response
          error.data = response
          throw error
        }

        return response
      } catch (error) {
        console.error('Delete Guest Activities error:', error)
        throw error
      }
    },
    async deleteEventGuest(config, guest_id) {
      const authStore = useAuthStore()
      const userToken = authStore.token
      try {
        const response = await $fetch(`${config.public.strapiApi}/${this.endpoint}/delete-registration-guests`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${userToken}`
          },
          body: {
            guest_id: guest_id
          }
        })
        return response
      } catch (error) {
        console.error(error?.response)
      }
    },
    async getEventActivities(config) {
      const authStore = useAuthStore()
      const userToken = authStore.token
      try {
        const response = await $fetch(`${config.public.strapiApi}/${this.endpoint}/get-activity-registration`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${userToken}`
          },
          body: {
            event_registration_id: this.registration.id
          }
        })
        this.activities = response
        return true
      } catch (error) {
        console.error(error?.response)
      }
    },
    async setEventActivities(config, activity) {
      const authStore = useAuthStore()
      const userToken = authStore.token
      const activityRaw = JSON.parse(JSON.stringify(activity))
      try {
        const response = await $fetch(`${config.public.strapiApi}/${this.endpoint}/set-activity-registration`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${userToken}`
          },
          body: {
            event_registration_id: this.registration.id,
            activity_id: activity.sugar_id,
            participants: activityRaw.participants
          }
        })
        if (response.error) {
          const error = new Error(response.error_message || response.error)
          error.errorCode = response.error
          error.formLabel = response.formLabel
          throw error
        }
        return response.id
      } catch (error) {
        console.error('setEventActivities error:', error)
        throw error
      }
    },
    async updateEventActivities(config, activityRegistrationId, seats) {
      const authStore = useAuthStore()
      const userToken = authStore.token
      try {
        const response = await $fetch(`${config.public.strapiApi}/${this.endpoint}/update-activity-registration`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${userToken}`
          },
          body: {
            activity_registration_id: activityRegistrationId,
            seats: seats
          }
        })
        return response
      } catch (error) {
        console.error('Update activity seats error:', error?.response)
        throw error
      }
    },
    async deleteEventActivities(config, activity_registration_id) {
      const authStore = useAuthStore()
      const userToken = authStore.token
      try {
        const response = await $fetch(`${config.public.strapiApi}/${this.endpoint}/delete-activity-registration`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${userToken}`
          },
          body: {
            activity_registration_id: activity_registration_id
          }
        })
        return response
      } catch (error) {
        console.error(error?.response)
      }
    }
  }
})
